import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaSearch, FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { Modal, Button, Form } from "react-bootstrap";
import showToast from "../Component/ToastNotification";
import { Toaster } from "react-hot-toast";

function SubCategoryList() {
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]); // 🔥 Store filtered subcategories
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [categoryId, setCategoryId] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [selectedSuperCategory, setSelectedSuperCategory] = useState("");
  const [categoryPhoto, setCategoryPhoto] = useState(null);
  const [imagePreview, setImagePreview] = useState(null); // For previewing the selected image

  useEffect(() => {
    fetchCategories();
  }, []);

const fetchCategories = async () => {
  const token = localStorage.getItem("authToken");
  if (!token) return;

  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/get-category`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) throw new Error("Failed to fetch categories");

    const result = await response.json();
    const categories = result.categories || [];
    setCategories(categories);

    const subCategoryList = categories.flatMap((category) => {
      let parsedSubCategories = [];

      if (typeof category.SubCategories === "string") {
        try {
          // Clean and prepare the string
          let raw = category.SubCategories.trim()
            .replace(/\\n/g, "")
            .replace(/\\"/g, '"')
            .replace(/\\'/g, "'")
            .replace(/\\\\/g, "\\");

          // Manually recover valid JSON objects from string
          const matches = raw.match(/{[^{}]*}/g); // all {...} objects
          if (matches) {
            parsedSubCategories = matches.map((obj) => JSON.parse(obj));
          }

        } catch (error) {
          console.warn(`⚠️ JSON parse failed for "${category.SuperCategoryName}"`);
          console.error("Error:", error.message);
        }
      }

      return parsedSubCategories.map((sub) => ({
        ...sub,
        SuperCategoryId: category.SuperCategoryId,
        SuperCategoryName: category.SuperCategoryName,
      }));
    });

    setSubCategories(subCategoryList);
  } catch (error) {
    console.error("❌ Error fetching categories:", error.message);
  }
};  

  const filteredSubCategories = subCategories.filter(
    (sub) =>
      sub.SubCategoryName &&
      sub.SubCategoryName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSubCategories = filteredSubCategories.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const handleModalClose = () => {
    resetForm();
    setShowModal(false);
  };

  const resetForm = () => {
    setCategoryName("");
    setCategoryId(null);
    setSelectedSuperCategory("");
    setImagePreview(null);  // <--- reset this as well!
    setIsEditing(false);
  };

const handleAddOrEditCategory = async () => {
  const token = localStorage.getItem("authToken");
  const formData = new FormData();

  formData.append("subcategory_name", categoryName);
  formData.append("parent_category_id", selectedSuperCategory);

  if (categoryId) {
    formData.append("ID", categoryId);
  }

  if (categoryPhoto) {
    formData.append("categoryPhoto", categoryPhoto);
  }

  try {
    const url = isEditing
      ? `${process.env.REACT_APP_API_URL}/update-subCategory`
      : `${process.env.REACT_APP_API_URL}/add-subCategory`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`, // no Content-Type here for FormData
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(
        isEditing ? "Failed to update category" : "Failed to add category"
      );
    }

    showToast(
      isEditing ? "Category updated successfully!" : "Category added successfully!"
    );

    fetchCategories();
    setShowModal(false);
    resetForm();
  } catch (error) {
    console.error("Error adding/updating category:", error);
    showToast("Something went wrong!", "error");
  }
};


  const totalPages = Math.ceil(filteredSubCategories.length / itemsPerPage);

  const handleEditClick = (category) => {
    setCategoryId(category.SubCategoryId);
    setCategoryName(category.SubCategoryName);
    setSelectedSuperCategory(category.SuperCategoryId)
    setImagePreview(category.SubCategoryIcon || null);  // <-- show existing image here
    setCategoryPhoto(null);  // clear file input, so user can optionally upload new image
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDeleteClick = (categoryId) => {
    setCategoryId(categoryId);
    setShowDeleteModal(true);
  };

  const handleDeleteCategory = async () => {
    const token = localStorage.getItem("authToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/delete-subCategory`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ID: categoryId }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete category");
      }
      showToast("Category deleted successfully!");

      fetchCategories();
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting category:", error);
      showToast("Failed to delete category!", "error");
    }
  };

      // Handle image change (for preview)
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCategoryPhoto(file);
            setImagePreview(URL.createObjectURL(file)); // Create preview URL for the image
        }
    };


  return (
    <div className="container" style={{ marginTop: "80px" }}>
      <Toaster position="top-right" reverseOrder={false} />
      <h2 className="text-primary">Subcategories List</h2>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="input-group me-3" style={{ maxWidth: "300px" }}>
          <span className="input-group-text bg-primary text-white">
            <FaSearch />
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Search subcategories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="btn btn-success" onClick={() => setShowModal(true)}>
          <FaPlus /> Add Category
        </button>
      </div>

      <table className="table table-bordered table-striped">
        <thead className="table-primary">
          <tr>
            <th>ID</th>
            <th>Subcategory Name</th>
            <th>Super Category</th>
            <th>Image</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentSubCategories.map((subcategory, index) => (
            <tr key={subcategory.SubCategoryId}>
              <td>{index + 1 + (currentPage - 1) * itemsPerPage}</td>
              <td>{subcategory.SubCategoryName}</td>
              <td>{subcategory.SuperCategoryName}</td>
              <td>
                <img
                  src={`${subcategory.SubCategoryIcon}`}
                  alt={subcategory.SubCategoryName} // better alt attribute
                  style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                />
              </td>
              <td>
                <button
                  className="btn btn-sm btn-outline-success me-2"
                  onClick={() => handleEditClick(subcategory)}
                >
                  <FaEdit /> Edit
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleDeleteClick(subcategory.SubCategoryId)}
                >
                  <FaTrash /> Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <nav>
        <ul className="pagination justify-content-center">
          {[...Array(totalPages)].map((_, index) => (
            <li
              key={index}
              className={`page-item ${
                currentPage === index + 1 ? "active" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => setCurrentPage(index + 1)}
              >
                {index + 1}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Model for edit */}
      <Modal show={showModal} onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? "Edit Category" : "Add New Category"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="categoryName">
              <Form.Label>Category Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter sub-category name"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="categoryPhoto" className="mt-3">
              <Form.Label>Category Photo</Form.Label>
              <Form.Control type="file" onChange={handleImageChange} />
              {imagePreview && <img src={imagePreview} alt="Preview" className="mt-3" style={{ width: '100px' }} />}
            </Form.Group>
            <Form.Group controlId="superCategory">
              <Form.Label>Super Category</Form.Label>
              <Form.Select
                value={selectedSuperCategory}
                onChange={(e) => setSelectedSuperCategory(e.target.value)}
              >
                <option value="">Select Super Category</option>
                {categories.map((category) => (
                  <option
                    key={category.SuperCategoryId}
                    value={category.SuperCategoryId}
                  >
                    {category.SuperCategoryName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            Close
          </Button>
          <Button variant="primary" onClick={handleAddOrEditCategory}>
            {isEditing ? "Update Category" : "Add Category"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* model for delete */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this category?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteCategory}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default SubCategoryList;